import React from 'react';
import ArchivingProfileStore from 'stores/ArchivingProfileStore';
import DepartmentFormStore from 'stores/department/DepartmentFormStore';
import RepProfilePage from './RepProfilePage';
import AltContainer from 'alt-container';
import urlWithParams from 'utils/urlWithParams';
import RepProfilePageActions from 'actions/RepProfilePageActions';
import RepProfilePageStore from 'stores/RepProfilePageStore';
import UserActions from 'actions/UserActions';
import SkillActions from 'actions/SkillActions';
import SkillStore from 'stores/SkillStore';
import TeamActions from 'actions/TeamActions';
import TeamListStore from 'stores/TeamListStore';
import LibraryActions from 'actions/library/LibraryActions';
import FileUploadCallDetailsActions from 'actions/files/FileUploadCallDetailsActions';
import {browserHistory} from 'react-router';
import connectToStores from 'alt-utils/lib/connectToStores';
import AuthStore from 'stores/AuthStore';
import FilePlayerWaveformStore from 'stores/files/FilePlayerWaveformStore';
import FileUploadCallCardStore from 'stores/files/FileUploadCallCardStore';
import LibraryCreateFormStore from 'stores/library/LibraryCreateFormStore';
import LibraryStore from 'stores/library/LibraryStore';
import ManagerDashboardStore from 'stores/ManagerDashboardStore';
import FileUploadStore from 'stores/files/FileUploadStore';
import UserStore from 'stores/UserStore';
import {List} from 'immutable';
import DashboardHistoryStore from 'stores/dashboard/DashboardHistoryStore';
import DashboardHistoryActions from 'actions/dashboard/DashboardHistoryActions';
import CoachingReviewTypeStore from 'stores/coaching/CoachingReviewTypeStore';
import CoachingReviewTypeActions from 'actions/coaching/CoachingReviewTypeActions';
import {getQuery} from 'utils/getQueryParam';
import {RepProfileTab, skillsFetchParametersShared} from '@store/coaching-plans';
import DashboardDateRangeStore, {
  dateRangeForDashboard,
  dateRangeForHeader,
} from 'stores/dashboard/DashboardDateRangeStore';
import FileUploadCallDetailsStore from 'stores/files/FileUploadCallDetailsStore';
import {DashboardTypeEnum, getDashboardType, HISTORY_ACTIONS} from 'components/StatsPage2.utils';

/**
 * Dates using non-standard (startDate,endDate] need to be corrected to [startDate + 1, endDate + 1)
 * before calling external actions or resources
 */
@connectToStores
export default class RepProfilePageContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    [
      'onToggleShowWeekends',
      'onSelectTeam',
      'onSelectRep',
      'onSelectTabIndex',
      'skillsFetchParameters',
      'fetchSkillTrends',
      'onSelectScoredByType',
    ].forEach(method => (this[method] = this[method].bind(this)));
  }

  static getStores() {
    return [DashboardDateRangeStore];
  }

  static getPropsFromStores() {
    const dateRangeState = DashboardDateRangeStore.getState();
    const {startDate, endDate} = dateRangeForDashboard(dateRangeState);
    const {startDate: headerStartDate, endDate: headerEndDate} = dateRangeForHeader(dateRangeState);
    return {
      startDate,
      endDate,
      headerStartDate,
      headerEndDate,
    };
  }

  loadManagerDashboard = () => {
    // @see Router.js to better understand this flag. Seems like it must be refactored somehow
    if (this.props?.route?.isGoHomeIfNotRep) {
      const user = AuthStore.getState().user;
      if (getDashboardType(user) !== DashboardTypeEnum.rep) {
        browserHistory.replace('/');
      }
    }
  };

  componentDidMount() {
    this.loadManagerDashboard();
    /* TODO refactor so Inbox, Admin, and Rep profile, which all share
       common styles, also use a common generic tab component
       (Calls tab should also be pulled into that fold)
       */
    CoachingReviewTypeActions.fetchAllReviewTypes();
    UserActions.fetchAll({ids: [AuthStore.getState().user.id]});
    LibraryActions.getShareAccessibleTeams();
    LibraryActions.getLibraries();
    LibraryActions.getTags();
    RepProfilePageActions.selectTabIndex(0);
    this.fetchParamDependentData();
    // Only when we enter this page from dashv2 do we need to push to the dashv2 history stack,
    // so no need to do this on update
    this.updateDashHistoryStack(this.props.location);
  }

  componentDidUpdate(prevProps) {
    this.loadManagerDashboard();
    if (this.props.location.search !== prevProps.location.search || this.datesChanged()) {
      this.fetchParamDependentData(prevProps);
    }
  }

  updateDashHistoryStack(location) {
    if (!location) return;
    const history = DashboardHistoryStore.getState().history;
    if (location.state && location.state.pathname) {
      const state = location.state;
      if (state && state.pathname) {
        // XXX just being defensive, can remove after testing
        if (!state.action) {
          console.warn('state.pathname is set but state.action is not', state.pathname);
        }
        if (state.action === HISTORY_ACTIONS.PUSH) {
          if (history.isEmpty() || history.last() !== state.pathname) {
            DashboardHistoryActions.pushHistory.defer(state.pathname);
          }
        }
        // This page should have no need to pop history (or reset)
      }
    }
  }

  datesChanged = (prevProps = {}) => {
    const {startDate, endDate} = this.props;
    const {startDate: prevStartDate, endDate: prevEndDate} = prevProps;
    return (
      !prevStartDate ||
      !prevEndDate ||
      !startDate.isSame(prevProps.startDate, 'day') ||
      !endDate.isSame(prevProps.endDate, 'day')
    );
  };

  // Front end really does not need to be using async here.  This is done so
  // backend is very sensitive to the request order and needs to be able to
  // use caches that it builds after the first request.  async/await causes
  // requests to run serially and not fire all at once
  async fetchParamDependentData(prevProps) {
    const {teamId, userId, tab, direction} = this.getParams(this.props);

    tab !== undefined && tab !== null && RepProfilePageActions.selectTabIndex(tab);

    const oldParams = prevProps ? this.getParams(prevProps) : {};
    if (teamId !== oldParams.teamId) {
      TeamActions.fetchUsers([teamId]);
    }

    const repChanged = userId !== oldParams.userId;
    const tabChanged = tab !== oldParams.tab;

    // entire page level data - if we changed date range / rep,
    // we need to reload these things
    let coachingPlansFetched = false;
    const dateRangeChanged = this.datesChanged(prevProps);
    if (dateRangeChanged || repChanged) {
      if (repChanged) {
        RepProfilePageActions.getCoachingPlans(userId);
        coachingPlansFetched = true;
      }
      this.fetchSkillTrends();
    }

    if (tabChanged && !coachingPlansFetched) {
      RepProfilePageActions.getCoachingPlans(userId);
    }

    // depending on the tab we're on, now load the tab dependent data
    const users = await UserActions.fetchAll({ids: List([userId])});
    const user = users.get(0);
    const oldTab = oldParams.tab;
    const {startDate, endDate} = this.props;
    this.getTabData(
      user,
      startDate,
      endDate,
      tab,
      direction,
      oldTab,
      dateRangeChanged || repChanged
    );
  }

  getTabData(user, inclStartDate, exclEndDate, tab, directionFromParams, oldTab, filtersChanged) {
    const {id: userId, teamId, managerId} = user;
    UserActions.fetchAll({teamId: teamId});

    if (!tab || tab === RepProfileTab.SUMMARY) {
      RepProfilePageActions.getSummary(userId, teamId, inclStartDate, exclEndDate);
      RepProfilePageActions.getCallMetrics({
        teamIds: [teamId],
        startDate: inclStartDate,
        endDate: exclEndDate,
        managerId,
      });
    }

    // if we just arrived (in the case of sub-tabs), load the default sub tab data
    const justLoadedTab = tab !== oldTab;

    if (tab === RepProfileTab.CALL_ACTIVITY) {
      RepProfilePageActions.getCallActivity(teamId, userId, inclStartDate, exclEndDate);
      RepProfilePageActions.getCallDonut(teamId, inclStartDate, exclEndDate);
      RepProfilePageActions.getCallTeamMetrics(
        teamId,
        userId,
        inclStartDate,
        exclEndDate,
        managerId
      );
    }

    if (tab === RepProfileTab.REVIEW_ACTIVITIES && (justLoadedTab || filtersChanged)) {
      const direction = directionFromParams ? directionFromParams : 'review';
      RepProfilePageActions.getCoachingActivity({
        direction,
        teamId,
        userId,
        startDate: inclStartDate,
        endDate: exclEndDate,
      });
      RepProfilePageActions.getCoachingTeamMetrics({
        direction,
        teamId,
        userId,
        startDate: inclStartDate,
        endDate: exclEndDate,
      });
    }

    if (tab === RepProfileTab.COACHING_PLANS) {
      // already have the data we need loaded for coaching skills / plans
    }

    if (tab === RepProfileTab.COACHING_RECEIVED && (justLoadedTab || filtersChanged)) {
      const direction = directionFromParams ? directionFromParams : 'manager';
      RepProfilePageActions.getCoachingActivity({
        direction,
        teamId,
        userId,
        startDate: inclStartDate,
        endDate: exclEndDate,
      });
      RepProfilePageActions.getCoachingTeamMetrics({
        direction,
        teamId,
        userId,
        startDate: inclStartDate,
        endDate: exclEndDate,
      });
    }

    if (tab === RepProfileTab.CALLS) {
      RepProfilePageActions.getTeamConversationTrend(teamId, inclStartDate, exclEndDate);
      RepProfilePageActions.getTeamCallDurationTrend(teamId, inclStartDate, exclEndDate);
    }
  }

  /**
   * Fetch skill trends. Used for skills tab
   *
   * @param startDate - override start if necessary
   * @param endDate - override end if necessary
   */
  fetchSkillTrends(startDate, endDate) {
    const {userId} = this.getParams(this.props);

    const fetchParams = this.skillsFetchParameters(
      userId,
      RepProfilePageStore.getState().selectedScoredByType
    );

    SkillActions.fetchUserTrend({
      ...fetchParams,
      from: fetchParams.from,
      to: fetchParams.to,
    }).then(teamTrends => {
      if (!teamTrends) return;

      // if they havent made a selection yet, change their scored by to default to ALL SCORES.
      // if they have selected a value, respect that intention.
      const {scoredByTypeWasSelected} = RepProfilePageStore.getState();
      if (!scoredByTypeWasSelected && teamTrends.length === 0) {
        this.onSelectScoredByType('ALL_SCORES');
      }
    });
  }

  skillsFetchParameters(userId, scoredBy) {
    const {startDate, endDate} = this.props;
    return skillsFetchParametersShared(userId, scoredBy, startDate, endDate);
  }

  onSelectRep(userId) {
    if (userId !== this.getParams(this.props).userId) {
      this.changeParams({userId});
      RepProfilePageActions.resetRepData();
    }
  }

  onSelectTeam(team, doRedirect = false) {
    // If this is called from didMount or didUpdate we do nothing (doRedirect needed by StatsPage2)
    // XXX maybe we should do something, see https://execvision.myjetbrains.com/youtrack/issue/EV-13897
    if (doRedirect) {
      this.changeParams({teamId: team.id});
    }
  }

  onToggleShowWeekends(showWeekends) {
    this.changeParams({showWeekends});
  }

  changeParams(newParams) {
    browserHistory.push(
      urlWithParams('/metrics/profile', {
        ...getQuery(this.props.location),
        // clear planId if there are any, newParams can put it back if needed
        planId: null,
        ...newParams,
      })
    );
  }

  onSelectTabIndex(selectedIndex) {
    RepProfilePageActions.selectTabIndex(selectedIndex);
    this.changeParams({tab: selectedIndex});
  }

  onSelectScoredByType(scoredBy) {
    const {userId} = this.getParams(this.props);
    SkillActions.fetchUserTrend.defer({
      ...this.skillsFetchParameters(userId, scoredBy),
    });
    RepProfilePageActions.selectScoredByType.defer(scoredBy);
  }

  getParams({location: {query}}) {
    const {teamId, userId, managerId, showWeekends, tab, direction} = {
      ...query,
    };
    return {
      teamId: teamId || AuthStore.getState().user.teamId,
      userId: userId || AuthStore.getState().user.id,
      managerId,
      showWeekends: showWeekends === 'true',
      tab: (tab !== null && tab !== undefined && parseInt(tab)) || RepProfileTab.SUMMARY,
      direction,
    };
  }

  render() {
    const params = this.getParams(this.props);
    return (
      <AltContainer
        stores={{
          ArchivingProfileStore,
          AuthStore,
          DepartmentFormStore,
          FilePlayerWaveformStore,
          FileUploadCallCardStore,
          FileUploadCallDetailsStore,
          FileUploadStore,
          LibraryCreateFormStore,
          LibraryStore,
          ManagerDashboardStore,
          RepProfilePageStore,
          SkillStore,
          TeamListStore,
          UserStore,
          CoachingReviewTypeStore,
        }}
        actions={{
          FileUploadCallDetailsActions,
          LibraryActions,
          RepProfilePageActions,
          SkillActions,
        }}>
        <RepProfilePage
          onSelectRep={this.onSelectRep}
          onSelectTeam={this.onSelectTeam}
          onSelectTabIndex={this.onSelectTabIndex}
          onToggleShowWeekends={this.onToggleShowWeekends}
          onSelectScoredByType={this.onSelectScoredByType}
          repId={params.userId}
          getCallDetails={this.getCallDetails}
          getInfoAboutFiles={LibraryStore.getInfoAboutFiles}
          location={this.props.location}
          startDate={this.props.startDate}
          endDate={this.props.endDate}
          headerStartDate={this.props.headerStartDate}
          headerEndDate={this.props.headerEndDate}
          {...params}
        />
      </AltContainer>
    );
  }
}
